var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { StardogSparqlParser } from '../sparql/StardogSparqlParser';
import { getFirstChildCstElementByRuleStack, isIToken, } from 'helpers/chevrotain/cst';
import { graphQlUtils } from 'graphql/utils';
var getArgumentNodes = graphQlUtils.getArgumentNodes, getArgumentTokenTypesForDirectiveNameToken = graphQlUtils.getArgumentTokenTypesForDirectiveNameToken, isSparqlReceivingStardogDirective = graphQlUtils.isSparqlReceivingStardogDirective;
function parseSparqlExpression(stringValueToken, stardogSparqlParser) {
    var innerExpressionImage = stringValueToken.image.slice(1, -1); // remove quotes
    return stardogSparqlParser.parse(innerExpressionImage, stardogSparqlParser.Expression);
}
// Make the embedded SPARQL errors have proper locations for use in error
// diagnostics. NOTE: This does NOT modify the locations of the error's
// `previousToken` property. If that ends up being needed, it's a TODO.
function mapSparqlErrors(sparqlErrors, tokenForOffset, offsetPadding) {
    if (offsetPadding === void 0) { offsetPadding = 0; }
    var tokenStartOffset = tokenForOffset.startOffset, tokenEndOffset = tokenForOffset.endOffset, tokenStartColumn = tokenForOffset.startColumn, tokenEndColumn = tokenForOffset.endColumn;
    return sparqlErrors.map(function (error) {
        var token = error.token;
        var errorStartOffset = token.startOffset, errorEndOffset = token.endOffset, errorStartColumn = token.startColumn, errorEndColumn = token.endColumn;
        return __assign({}, error, { token: __assign({}, token, { 
                // error token's offsets might be set explicitly to null
                startOffset: tokenStartOffset + (errorStartOffset || 0) + offsetPadding, endOffset: tokenEndOffset + (errorEndOffset || 0) + offsetPadding, startColumn: tokenStartColumn + (errorStartColumn || 0) + offsetPadding, endColumn: tokenEndColumn + (errorEndColumn || 0) + offsetPadding, startLine: tokenForOffset.startLine, endLine: tokenForOffset.endLine }) });
    });
}
// Checks that the right number of arguments are supplied, accumulating errors
// otherwise.
function validateDirectiveArgumentsArity(_a) {
    var allowedArgumentTokenTypes = _a.allowedArgumentTokenTypes, directiveImage = _a.directiveImage, errorAccumulator = _a.errorAccumulator, numMinimumArguments = _a.numMinimumArguments, suppliedArgumentNodes = _a.suppliedArgumentNodes;
    var numSuppliedArguments = suppliedArgumentNodes.length;
    var numAllowedArguments = allowedArgumentTokenTypes.length;
    var validArgumentsPhrase = "valid arguments: " + allowedArgumentTokenTypes
        .map(function (argumentTokenType) { return "`" + argumentTokenType.PATTERN + "`"; })
        .join(', ');
    var errorTypeName = 'ArgumentArityError';
    if (numSuppliedArguments < numMinimumArguments) {
        var requiresPhrase = "requires " + numMinimumArguments + " argument" + (numMinimumArguments > 1 ? 's' : '');
        errorAccumulator.stardogGraphQlErrors.push({
            name: errorTypeName,
            message: "The " + directiveImage + " directive " + requiresPhrase + " (" + validArgumentsPhrase + ")",
        });
    }
    else if (numSuppliedArguments > numAllowedArguments) {
        errorAccumulator.stardogGraphQlErrors.push({
            name: errorTypeName,
            message: "Too many arguments provided to " + directiveImage + " directive (" + validArgumentsPhrase + ")",
        });
    }
}
// Checks that the supplied argument names and values conform to the allowed
// arguments of a directive and have valid embedded SPARQL.
function validateDirectiveArgumentsNameAndValue(_a) {
    var allowedArgumentTokenTypes = _a.allowedArgumentTokenTypes, directiveImage = _a.directiveImage, errorAccumulator = _a.errorAccumulator, sparqlParser = _a.sparqlParser, suppliedArgumentNodes = _a.suppliedArgumentNodes;
    var validArgumentsPhrase = "valid arguments: " + allowedArgumentTokenTypes
        .map(function (argumentTokenType) { return "`" + argumentTokenType.PATTERN + "`"; })
        .join(', ');
    suppliedArgumentNodes.forEach(function (argumentNode) {
        var _a;
        var argumentNameToken = getFirstChildCstElementByRuleStack(['Alias', 'Name'], argumentNode);
        if (!isIToken(argumentNameToken)) {
            // This should never be an actual possibility, but we'll be safe in
            // case it somehow occurs.
            return;
        }
        var argumentTokenTypeName = argumentNameToken.tokenType.name;
        var isAllowedArgument = allowedArgumentTokenTypes.some(function (argumentTokenType) { return argumentTokenType.name === argumentTokenTypeName; });
        if (!isAllowedArgument) {
            errorAccumulator.stardogGraphQlErrors.push({
                name: 'InvalidArgumentError',
                message: "Invalid argument `" + argumentNameToken.image + "` for " + directiveImage + " directive (" + validArgumentsPhrase + ")",
            });
        }
        else {
            // For allowed arguments, we provide embedded SPARQL parsing. Note
            // again that we are currently *only* handling Stardog-specific
            // directives that receive SPARQL here.
            var argumentStringValueToken = getFirstChildCstElementByRuleStack(['Value', 'StringValue', 'StringValueToken'], argumentNode);
            if (!isIToken(argumentStringValueToken)) {
                // As noted above, we are currently only checking string arguments to
                // ensure they parse as valid SPARQL expressions. We bail on
                // non-strings.
                return;
            }
            var errors = parseSparqlExpression(argumentStringValueToken, sparqlParser).errors;
            // Possible future TODO: replace the CST nodes with those returned
            // from the stardogSparqlParser, like we do for the IfClause and
            // ThenClause in the SRS Parser
            if (errors.length > 0) {
                (_a = errorAccumulator.sparqlErrors).push.apply(_a, mapSparqlErrors(errors, argumentStringValueToken, 1));
            }
        }
    });
}
function validateDirectiveArguments(_a) {
    var validatorOptions = _a.validatorOptions, validators = _a.validators;
    validators.forEach(function (validator) { return validator(validatorOptions); });
}
// Delegates to the appropriate validator for a specific directive.
function validateSuppliedArgumentsForDirective(suppliedArgumentNodes, directiveNameToken, sparqlParser) {
    var errorAccumulator = {
        stardogGraphQlErrors: [],
        sparqlErrors: [],
    };
    var allowedArgumentTokenTypes = getArgumentTokenTypesForDirectiveNameToken(directiveNameToken);
    var directiveImage = directiveNameToken.tokenType.PATTERN;
    validateDirectiveArguments({
        validatorOptions: {
            allowedArgumentTokenTypes: allowedArgumentTokenTypes,
            directiveImage: directiveImage,
            errorAccumulator: errorAccumulator,
            numMinimumArguments: 1,
            sparqlParser: sparqlParser,
            suppliedArgumentNodes: suppliedArgumentNodes,
        },
        // We do things this way to be "forward-looking." In the future, we may
        // want to define custom validators for certain directives that have
        // special rules. In that case, we could add custom validators here based
        // on the type of the `directiveNameToken`.
        validators: [
            validateDirectiveArgumentsArity,
            validateDirectiveArgumentsNameAndValue,
        ],
    });
    return errorAccumulator;
}
// Returns a custom visitor that extends the BaseVisitor for the
// StardogGraphQlParser. When the visitor encounters any custom Stardog
// directive that can contain a SPARQL expression, it locates the expression
// and ensures that it parses as valid SPARQL (by delegating to the
// StardogSparqlParser). Similarly, the visitor applies some special checks
// (with corresponding error messages) for Stardog-specific GraphQL directives
// that are not built into the GraphQL grammar itself.
export var getStardogGraphQlVisitor = function (BaseVisitor, parserInstance) {
    var stardogSparqlParser = new StardogSparqlParser(); // for parsing embedded SPARQL;
    var StardogGraphQlVisitor = /** @class */ (function (_super) {
        __extends(StardogGraphQlVisitor, _super);
        function StardogGraphQlVisitor() {
            var _this = _super.call(this) || this;
            _this.sparqlErrors = [];
            _this.stardogGraphQlErrors = [];
            _this.visit = function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                _super.prototype.visit.apply(_this, args);
                return {
                    sparqlErrors: _this.sparqlErrors,
                    stardogGraphQlErrors: _this.stardogGraphQlErrors,
                };
            };
            _this.Directive = function (ctx) {
                var _a, _b;
                if (!ctx.Name || !ctx.Name[0]) {
                    // Somehow we have a directive with no Name token. This shouldn't be
                    // possible, but we check just to be safe (and bail otherwise).
                    return;
                }
                var directiveNameToken = ctx.Name[0];
                // NOTE: We currently only provide special handling for Stardog-specific
                // directives that accept SPARQL, so we bail early in all other cases.
                // We may want to expand this later.
                if (!isSparqlReceivingStardogDirective(directiveNameToken)) {
                    return;
                }
                var suppliedArguments = getArgumentNodes(ctx);
                var accumulatedErrors = validateSuppliedArgumentsForDirective(suppliedArguments, directiveNameToken, stardogSparqlParser);
                if (accumulatedErrors.stardogGraphQlErrors.length > 0) {
                    // The accumulated errors up to this point do not have stack
                    // information. We add it here (only once, for performance reasons).
                    var ruleStack_1 = parserInstance.getHumanReadableRuleStack();
                    var ruleOccurrenceStack_1 = parserInstance
                        .RULE_OCCURRENCE_STACK;
                    (_a = _this.stardogGraphQlErrors).push.apply(_a, accumulatedErrors.stardogGraphQlErrors.map(function (partialError) { return (__assign({}, partialError, { token: directiveNameToken, context: {
                            ruleStack: ruleStack_1,
                            ruleOccurrenceStack: ruleOccurrenceStack_1,
                        }, resyncedTokens: [] })); }));
                }
                (_b = _this.sparqlErrors).push.apply(_b, accumulatedErrors.sparqlErrors.map(function (sparqlError) { return (__assign({}, sparqlError, { name: "SPARQL Error: " + sparqlError.name })); }));
            };
            _this.$resetState = function () {
                _this.stardogGraphQlErrors = [];
                _this.sparqlErrors = [];
            };
            _this.validateVisitor();
            return _this;
        }
        return StardogGraphQlVisitor;
    }(BaseVisitor));
    return new StardogGraphQlVisitor();
};
